import type { Segment } from "@domains/segments/shared/segmentsTypes.ts";
import { Badge } from "@/components/ui/badge.tsx";
import { Note } from "@/components/ui/note.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ListBox, ListBoxItem, ListBoxSection } from "@/components/ui/list-box.tsx";
import { ProgressCircle } from "@/components/ui/progress-circle.tsx";
import { SearchField, SearchInput } from "@/components/ui/search-field.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useSegmentsClient } from "@domains/segments/client/api/segmentsQuery.ts";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { useInfiniteCollection } from "@packages/core-client/collection/useInfiniteCollection.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useState } from "react";
import { Collection, Header, ListBoxLoadMoreItem, type Key } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useTeamAdHocFilter } from "../../hooks/teamAdHocFilter.ts";
import { useTeamSegment } from "../../hooks/useTeamSegment.ts";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";

const SEARCH_DEBOUNCE_MS = 200;

const SELECTED_ROW_CLASS_NAME = "[&>svg[data-slot=check-indicator]]:hidden selected:bg-primary-subtle";

const SEGMENT_PLACEHOLDER: Segment[] = [
    { id: "skeleton-1", name: "Xxxxxxxx Xxxxx", tags: [], isEditable: true },
    { id: "skeleton-2", name: "Xxxx Xxxxxxxxx", tags: [], isEditable: true },
    { id: "skeleton-3", name: "Xxxxxxx Xxxxxxxx", tags: [], isEditable: false }
];

interface SegmentNameProps {
    name: string;
    caption?: string;
    appliedLabel?: string;
    isLoading: boolean;
}

function SegmentName({ name, caption, appliedLabel, isLoading }: SegmentNameProps) {
    return (
        <Skeleton isLoading={isLoading} inert={isLoading} aria-hidden={isLoading} className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-col">
                <div className="flex items-center gap-2">
                    <div className="min-w-0 truncate text-sm font-medium">
                        <span>{name}</span>
                    </div>
                    {appliedLabel && <Badge intent="primary">{appliedLabel}</Badge>}
                </div>
                {caption && (
                    <div className="truncate">
                        <span className="text-muted-fg text-xs">{caption}</span>
                    </div>
                )}
            </div>
        </Skeleton>
    );
}

export interface ManageSegmentsSidebarProps {
    selectedKey: string;
    onSelect: (key: string) => void;
}

export function ManageSegmentsSidebar({ selectedKey, onSelect }: ManageSegmentsSidebarProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ManageSegmentsSidebar", { i18n: i18nextInstance });
    const session = useSession();
    const { queryUtils } = useSegmentsClient();
    const organizationId = session?.organizationId ?? "";
    const [appliedSegmentId] = useTeamSegment();
    const adHocFilter = useTeamAdHocFilter();

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
    const trimmedSearchTerm = debouncedSearchTerm.trim();

    const fetchPage = useCallback(({ continuationToken }: { continuationToken: string | undefined }) => queryUtils.listVisibleSegments.call({ orgId: organizationId, q: trimmedSearchTerm || undefined, continuationToken }), [queryUtils, organizationId, trimmedSearchTerm]);
    const getLoadedMoreMessage = useCallback((count: number) => t("liveRegion.loadedMore", { count }), [t]);
    const getRefreshedMessage = useCallback((count: number) => t("liveRegion.results", { count }), [t]);

    const { items, isLoading, isEmpty, isColdError, hasActiveFilters, hasNextPage, isFetchingNextPage, loadMore, refetch, loadedAnnouncement, refreshedAnnouncement } = useInfiniteCollection<Segment>({
        queryKey: [...queryUtils.listVisibleSegments.key(), { orgId: organizationId, q: trimmedSearchTerm }],
        queryFn: continuationToken => fetchPage({ continuationToken }),
        placeholder: SEGMENT_PLACEHOLDER,
        hasActiveFilters: trimmedSearchTerm !== "",
        getLoadedMoreMessage,
        getRefreshedMessage,
        onError: "inline"
    });

    const handleRetry = useCallback(() => refetch(), [refetch]);
    const handleSelectionChange = useCallback(
        (keys: "all" | Set<Key>) => {
            if (keys === "all") {
                return;
            }

            const [key] = keys;
            if (key !== undefined) {
                onSelect(String(key));
            }
        },
        [onSelect]
    );

    const myItems = items.filter(segment => segment.isEditable);
    const sharedItems = items.filter(segment => !segment.isEditable);
    const emptyMessage = hasActiveFilters ? t("noResults") : t("empty");

    const renderMyItem = useCallback(
        (segment: Segment) => (
            <ListBoxItem key={segment.id} id={segment.id} textValue={segment.name} className={SELECTED_ROW_CLASS_NAME}>
                <span className="col-span-full flex min-w-0">
                    <SegmentName name={segment.name} caption={segment.tags.length > 0 ? segment.tags.join(", ") : undefined} appliedLabel={segment.id === appliedSegmentId ? t("applied") : undefined} isLoading={isLoading} />
                </span>
            </ListBoxItem>
        ),
        [isLoading, appliedSegmentId, t]
    );
    const renderSharedItem = useCallback(
        (segment: Segment) => (
            <ListBoxItem key={segment.id} id={segment.id} textValue={segment.name} className={SELECTED_ROW_CLASS_NAME}>
                <span className="col-span-full flex min-w-0">
                    <SegmentName name={segment.name} caption={segment.owner ? t("sharedBy", { name: segment.owner.displayName }) : undefined} appliedLabel={segment.id === appliedSegmentId ? t("applied") : undefined} isLoading={isLoading} />
                </span>
            </ListBoxItem>
        ),
        [isLoading, appliedSegmentId, t]
    );

    return (
        <div className="bg-muted flex h-full flex-col">
            <p className="px-3 pt-3 text-base font-semibold">{t("heading")}</p>
            <div className="flex flex-col gap-2 p-2">
                <SearchField aria-label={t("searchPlaceholder")} value={searchTerm} onChange={setSearchTerm} className="[--control-bg:var(--color-bg)]">
                    <SearchInput placeholder={t("searchPlaceholder")} />
                </SearchField>
            </div>

            <output className="sr-only">{isLoading && t("liveRegion.loading")}</output>
            <output className="sr-only">{loadedAnnouncement}</output>
            <output className="sr-only">{refreshedAnnouncement}</output>

            <div className="min-h-0 flex-1 overflow-y-auto">
                <ListBox aria-label={t("listAriaLabel")} selectionMode="single" selectedKeys={new Set([selectedKey])} onSelectionChange={handleSelectionChange} escapeKeyBehavior="none" className="max-h-none overflow-visible rounded-none border-0 bg-transparent">
                    <ListBoxItem id="new" textValue={t("newSegment")} className={SELECTED_ROW_CLASS_NAME}>
                        <span className="col-span-full flex items-center gap-2">
                            <PlusIcon aria-hidden="true" className="size-4" />
                            <span className="text-sm font-medium">{t("newSegment")}</span>
                        </span>
                    </ListBoxItem>

                    {adHocFilter && (
                        <ListBoxItem id="current-filters" textValue={t("currentFilters")} className={SELECTED_ROW_CLASS_NAME}>
                            <span className="col-span-full flex min-w-0">
                                <SegmentName name={t("currentFilters")} caption={t("currentFiltersCaption", { count: adHocFilter.rules.length })} isLoading={false} />
                            </span>
                        </ListBoxItem>
                    )}

                    {myItems.length > 0 && (
                        <ListBoxSection>
                            <Header className="text-muted-fg col-span-full px-3 py-2 text-xs/3 font-medium tracking-wide uppercase sm:px-2.5 sm:py-1.5">{t("mySegments")}</Header>
                            <Collection items={myItems} dependencies={[isLoading]}>
                                {renderMyItem}
                            </Collection>
                        </ListBoxSection>
                    )}

                    {sharedItems.length > 0 && (
                        <ListBoxSection>
                            <Header className="text-muted-fg col-span-full px-3 py-2 text-xs/3 font-medium tracking-wide uppercase sm:px-2.5 sm:py-1.5">{t("sharedWithMe")}</Header>
                            <Collection items={sharedItems} dependencies={[isLoading]}>
                                {renderSharedItem}
                            </Collection>
                        </ListBoxSection>
                    )}

                    {isEmpty && !isColdError && (
                        <ListBoxItem id="no-segments-message" textValue={emptyMessage} isDisabled>
                            <span className="col-span-full text-sm">{emptyMessage}</span>
                        </ListBoxItem>
                    )}

                    {hasNextPage && !isLoading && (
                        <ListBoxLoadMoreItem onLoadMore={loadMore} isLoading={isFetchingNextPage}>
                            <div className="flex items-center justify-center gap-2 py-2 text-sm">
                                <ProgressCircle isIndeterminate aria-label={t("loadMore")} className="text-muted-fg size-4" />
                                <span className="text-muted-fg">{t("loadMore")}</span>
                            </div>
                        </ListBoxLoadMoreItem>
                    )}
                </ListBox>

                {isColdError && (
                    <div className="space-y-2 p-2">
                        <Note intent="danger" role="alert">
                            {t("loadError")}
                        </Note>
                        <Button intent="outline" size="sm" onPress={handleRetry} className="w-full">
                            {t("retry")}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
